// Typed wrappers over the broker submission BFF (PMS-S125, now live). A
// submission is per-product/coverage line, own-scoped to the session brokerage.
// Mirrors the shape of the other broker seams (src/lib/brokerAccounts.ts): thin
// functions over the authed requester, types derived from the PMS contract
// (docs/openapi.yaml → brokerSubmissionDraftSchema + the enriched list item).
import type { LossRuns } from "@/lib/submissions/lossRuns";
import {
  readCarrierProfile,
  type BrokerCarrierSummary,
} from "@/lib/brokerCarriers";
import type {
  BillingType,
  BrokerBindRequest,
  PaymentPlan,
} from "@/lib/submissions/quoteBinding";
import { ApiError, type FileResponse } from "@/lib/api";
import { messageFor } from "@/lib/errors";
import type { AuthedBlobRequester, AuthedRequester } from "@/lib/brokerAuth";
import {
  readActionStanding,
  type ActionStanding,
} from "@/lib/submissions/actionItems";
import type { Page } from "@/lib/brokerAccounts";
import type { FieldType, MissingField } from "@/lib/brokerProducts";
import type { OfficerTitle } from "@/lib/officerTitles";
import type { PersonnelRole } from "@/lib/personnelRoles";
import type { PhoneDetail, PhoneInput } from "@/lib/phone";
import type {
  BillingElection,
  CoverageTerms,
} from "@/lib/submissions/coverageTerms";
import type {
  QuoteNote,
  QuoteReferral,
  QuoteSubjectivity,
  QuoteVersionSummary,
} from "@/lib/submissions/quoteConditions";
import type { PriorCoverageRow } from "@/lib/submissions/priorCoverage";
import type { ReviewRequest } from "@/lib/submissions/quoteReview";

export type { MissingField };

const BASE = "/api/broker/submissions";

/** Submission lifecycle status (mirrors the PMS submission_status enum). */
export type SubmissionStatus =
  | "draft"
  | "awaiting_triage"
  | "in_review"
  | "requested_info"
  | "awaiting_approval"
  | "quoted"
  | "bound"
  | "declined"
  | "deferred";

/** Every status, in PMS's order: the runtime copy of `SubmissionStatus` the
 *  list's Status filter offers (BRK-S254), held to the pinned enum by
 *  brokerSubmissionsContract.test.ts. */
export const SUBMISSION_STATUSES: readonly SubmissionStatus[] = [
  "draft",
  "awaiting_triage",
  "in_review",
  "requested_info",
  "awaiting_approval",
  "quoted",
  "bound",
  "declined",
  "deferred",
];

/**
 * A created submission. PMS returns the full submission detail; the intake flow
 * only needs the confirmation fields (the SUB number + status).
 */
export interface Submission {
  id: string;
  submissionNumber: string;
  accountId: string;
  status: SubmissionStatus;
  createdAt: string;
}

/**
 * A prior submission on an account, enriched for the renewal picker
 * (PMS-S125 brokerSubmissionListItem): human coverage label + line key + display
 * limit + effective/expiration + status, and the resulting carrier once the
 * submission is quoted/bound (null until then). PMS submissions are single-layer
 * — there is no primary/excess "layer".
 */
export interface AccountSubmission {
  id: string;
  submissionNumber: string;
  accountId: string;
  accountLegalName: string | null;
  coverage: string;
  coverageKey: string;
  projectedLimit: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  status: SubmissionStatus;
  carrier: string | null;
  /** The bound carrier's id — PMS's identity for it (the name above is now
   *  display only, deprecated upstream). Null until a carrier is bound.
   *  Arrived on the bond pin's copy of this list at BRK-S511. */
  carrierId: string | null;
  createdAt: string;
  /** When it was last modified (PMS-S589): the list's default order and its
   *  Last modified column. */
  updatedAt: string;
  /** When the broker first submitted it to Signal; null while a draft
   *  (PMS-S708). Published on the list row since the sprint-10 release re-pin;
   *  read, not yet rendered by the list. Optional: an older PMS omits it. */
  submittedAt?: string | null;
  /** Where its loss runs stand (PMS-S748). Published on the list row since the
   *  sprint-10 release re-pin; read, not yet rendered by the list (the detail's
   *  panel is BRK-S361's). The row carries no `notAvailable`. Optional, as
   *  above. */
  lossRuns?: Pick<LossRuns, "required" | "status" | "count">;
}

/**
 * The draft-create body (PMS-S125, product-addressed in PMS-S147). A submission
 * starts as a minimal draft on an EXISTING own account. The portal always sends
 * `productId` when a product was picked (a coverage line can carry several
 * products, so a bare `line` — still accepted by PMS — can be ambiguous); a
 * renewal names only the prior submission and PMS copies its product AND
 * carries its field bag forward (PMS-S148). `brokerageId` is never sent — PMS
 * forces it from the session.
 */
export interface CreateSubmissionInput {
  accountId: string;
  productId?: string;
  line?: string;
  renewalOfSubmissionId?: string;
}

/** POST /api/broker/submissions — start one draft submission (own-scoped). */
export function createSubmission(
  request: AuthedRequester,
  input: CreateSubmissionInput,
): Promise<Submission> {
  return request<Submission>(BASE, { method: "POST", body: input });
}

/** GET /api/broker/submissions?accountId=… — prior submissions for the renewal picker. */
export function listAccountSubmissions(
  request: AuthedRequester,
  accountId: string,
): Promise<{ items: AccountSubmission[] }> {
  return request<{ items: AccountSubmission[] }>(
    `${BASE}?accountId=${encodeURIComponent(accountId)}`,
  );
}

/**
 * The cheap existence check for a duplicate DRAFT (BRK-S063) — before starting a
 * new submission on the same account + product, ask whether one is already in
 * flight. Uses the PMS-S151 own-scoped `productId` + `status` filters and caps
 * at one row (we only need existence + a link target). Returns the first
 * matching draft, or null. A failed check is the caller's to swallow — it must
 * never block a legitimate create (NN #7).
 */
export async function findExistingDraft(
  request: AuthedRequester,
  accountId: string,
  productId: string,
): Promise<AccountSubmission | null> {
  const qs = new URLSearchParams({
    accountId,
    productId,
    status: "draft",
    limit: "1",
  });
  const page = await request<Page<AccountSubmission>>(
    `${BASE}?${qs.toString()}`,
  );
  return page.items[0] ?? null;
}

/** What the submissions list asks PMS for (BRK-S254). Search matches the
 *  submission number and the insured's name or number; every filter narrows
 *  within the brokerage's own submissions. */
export interface SubmissionListQuery {
  search?: string;
  status?: SubmissionStatus;
  productId?: string;
  /** `updatedAt` (last modified, PMS-S589) or `createdAt`. */
  field?: "updatedAt" | "createdAt";
  dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

/** GET /api/broker/submissions — the brokerage's own submissions (enriched, paged). */
export function listSubmissions(
  request: AuthedRequester,
  query: SubmissionListQuery = {},
): Promise<Page<AccountSubmission>> {
  const qs = new URLSearchParams();
  if (query.search) qs.set("search", query.search);
  if (query.status) qs.set("status", query.status);
  if (query.productId) qs.set("productId", query.productId);
  if (query.field) qs.set("field", query.field);
  if (query.dir) qs.set("dir", query.dir);
  if (query.limit != null) qs.set("limit", String(query.limit));
  if (query.offset != null) qs.set("offset", String(query.offset));
  const s = qs.toString();
  return request<Page<AccountSubmission>>(`${BASE}${s ? `?${s}` : ""}`);
}

/**
 * The submission detail (PMS-S125 GET /{id}) — the subset the detail view
 * shows. The full PMS record carries much more; the broker view needs the
 * header (number/status/dates), the account + product refs, and — since
 * PMS-S142 — the manifest FIELD BAG (`data`), which prefills the details step.
 * Bag keys/value shapes are declared by the product manifest (BRK-S051).
 * `carriedForwardKeys` (PMS-S148) names the bag keys a RENEWAL copied from the
 * prior submission (term dates never carry) — the details step marks them for
 * review-not-re-entry; empty on a fresh submission.
 */
export interface SubmissionDetail {
  id: string;
  submissionNumber: string;
  status: SubmissionStatus;
  /** When the broker first submitted it to Signal; null while a draft
   *  (PMS-S708). A draft becomes Awaiting triage only by Submit. */
  submittedAt: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  account: { id: string; accountNumber: string; legalName: string } | null;
  product: { id: string; name: string } | null;
  data: Record<string, unknown>;
  /** PMS-S150: canonical Code id → {id, code, description} for every classCode-
   *  typed value anywhere in `data` (a top-level classCode field or a classCode
   *  item inside a group, e.g. WC `exposures[].codeId`). Lets the details step
   *  render carried-forward class codes as code + description instead of the raw
   *  uuid (the renewal UUID bug). Additive + read-only: the raw ids stay in
   *  `data`, so `PUT …/details` still round-trips. Unresolvable ids are omitted. */
  dataDisplayRefs: Record<string, CodeDisplayRef>;
  /** The field bag's payroll rows as PMS resolves them, each with its code.
   *  Read only for each code's wage tier (`codeWagesFrom`, BRK-S394), which
   *  the display refs don't carry. Optional for a PMS before S817. */
  exposures?: { codeId: string | null; code: Partial<CodeOption> | null }[];
  carriedForwardKeys: string[];
  /** Whether the insured is still only this submission's: created with it,
   *  with nothing else on record (no other submission, no bond order, no
   *  bind). While it is, the wizard edits the insured in real time, active or
   *  not (Nav, 2026-10-08); after, its information changes only by
   *  endorsement. PMS-S741, pinned; still read as optional, because a PMS
   *  before it (prod, until it deploys there) leaves it out, and the portal
   *  then falls back to the account's draft status. */
  insuredEditable?: boolean;
  /** Where its loss runs stand (PMS-S748, BRK-S361). Optional for the deploy
   *  window: a submission without it shows no loss runs prompt, never a false
   *  "required". */
  lossRuns?: LossRuns;
  /** A Workers' Comp submission's prior coverage by year, oldest term first
   *  (PMS-S810, BRK-S389). Optional for the deploy window: a PMS before it
   *  leaves it out, and the page then starts with none. */
  priorCoverage?: PriorCoverageRow[];
  /** Worked out from those rows, never asked: null without a current carrier
   *  or rows. */
  yearsWithCurrentCarrier?: number | null;
  /** Worked out from those rows, never asked (PMS-S909): Years 1 to 3's
   *  claims added up, and whether there were any; null without rows or
   *  while one of those has no count. */
  wcClaimsTotal3Years?: number | null;
  hadWcClaims3Years?: boolean | null;
  /** Who confirmed the ACORD 130 answers are true and correct, and when
   *  (PMS-S812); null while not confirmed. Read-only: PMS stamps it when
   *  `acord130Confirmed` is saved true and clears it when an answer
   *  changes. Optional for the deploy window. */
  acord130Confirmation?: {
    confirmedAt: string;
    confirmedBy: { id: string; name: string } | null;
  } | null;
}

/** A resolved class-code reference (PMS-S150) — the human code + description for
 *  a canonical Code id stored in the submission field bag. */
export interface CodeDisplayRef {
  id: string;
  code: string;
  description: string;
}

/** GET /api/broker/submissions/{id} — one of the brokerage's own submissions. */
export function getSubmission(
  request: AuthedRequester,
  id: string,
): Promise<SubmissionDetail> {
  return request<SubmissionDetail>(`${BASE}/${encodeURIComponent(id)}`);
}

// ─── Quoting (PMS-S125 PR-2) ──────────────────────────────────────────────────
// The broker triggers a price indication and reads the persisted quotes. The
// broker never names a carrier — PMS fans the indication out across every
// eligible carrier server-side (PMS-S143). Types mirror the PMS wire schemas.

/** An installment option on a quote. */
export interface BrokerPaymentPlan {
  /** Deprecated by PMS-S553 for `plan`, and still sent. */
  planType: "full" | "monthly" | "quarterly" | "semiannual" | null;
  /** The plan as a billing election names it (BRK-S209); optional, as a PMS
   *  before S553 doesn't send it. */
  plan?: PaymentPlan | null;
  downPayment: string | null;
  installmentAmount: string | null;
  numberOfInstallments: number | null;
}

/** One RESOLVED offer-term row (PMS-S143): the product manifest's display
 *  schema evaluated against the submission's field bag. `type` reuses the
 *  manifest field types, so the table formats values consistently. */
export interface OfferTermView {
  key: string;
  label: string;
  type: FieldType;
  value: string | number | boolean | string[] | null;
}

/** An ordered group of resolved offer-term rows (the comparison table spine). */
export interface OfferTermsGroupView {
  label: string;
  terms: OfferTermView[];
}

/** A quote as the broker sees it: kind-badged, the financial core + plans, and
 *  the manifest-resolved `offerTerms` (PMS-S143 — replaces the WC-hardcoded
 *  offer-terms superset). Terms describe the insured's requested terms, so
 *  they're the same across a submission's quotes; nullable values render as
 *  "—". */
export interface BrokerQuote {
  id: string;
  kind: "indication" | "quote";
  status: "active" | "expired" | "selected" | "withdrawn";
  carrier: string | null;
  carrierQuoteNumber: string | null;
  carrierProfile?: BrokerCarrierSummary | null;
  totalPremium: string;
  taxesAndFees: string;
  totalCost: string;
  elEachAccident: string | null;
  elEachEmployee: string | null;
  elEachPolicy: string | null;
  policyEffectiveDate: string | null;
  policyExpirationDate: string | null;
  quoteValidUntil: string | null;
  bindStatus: "quotable" | "refer" | "decline" | null;
  isSelected: boolean;
  createdAt: string;
  paymentPlans: BrokerPaymentPlan[];
  offerTerms: OfferTermsGroupView[];
  /** The application changed after this was priced (PMS-S707): it is no
   *  longer an offer. A quote on an outdated indication is refused (409
   *  `quote_outdated`); an outdated quote is priced again by asking
   *  again. Optional, as a PMS before S707 doesn't send it. A carrier that
   *  prices only when it quotes also says its price is stale (PMS-S750: Great
   *  American's PRICING_REFRESH_REQUIRED, after any change to what it holds,
   *  its own questions' answers included). */
  outdated?: boolean;
  /** The carrier has no indication step and prices only when it quotes
   *  (PMS-S750; Great American). Its `kind` is its own word, so that
   *  step can price an indication, and asking again only re-prices it:
   *  offered only while `outdated`, otherwise its underwriter's (BRK-S364).
   *  Optional, as a PMS before S750 doesn't send it. */
  pricesOnlyAtQuote?: boolean;
  // ── The unified quote (PMS-S553, filled by PMS-S745; BRK-S346) ─────────────
  // Each optional for the deploy window: a PMS before S553 sends none.
  /** Its coverage parts, itemized charges and totals. */
  terms?: CoverageTerms;
  /** The current version's referral reasons, as the carrier sent them; [] when
   *  it gave none, so a reload keeps a referral's reason. */
  referReasons?: string[];
  /** Its carrier, as the fan-out's legs name it: a saved quote addresses its
   *  carrier with no markets' check first. */
  carrierId?: string;
  /** The broker's billing choices, for select & bind (BRK-S209). */
  billing?: BillingElection;
  /** What the carrier takes at select and bind (PMS-S933, BRK-S510): its
   *  Carrier Payment Setup, else both billing types; the quote's own plans
   *  in that setup, else the setup's, else every plan. PMS refuses anything
   *  else. Optional for a PMS before S933, which the bind view reads as
   *  before. `offeredPlans` can be [] (nothing in common). */
  offeredBillingTypes?: BillingType[];
  offeredPlans?: PaymentPlan[];
  /** Where the broker is with the quote. */
  stage?: QuoteStage;
  /** What Bind does (PMS-S555): `request` asks Signal to bind it (every
   *  carrier today), `none` while it can't be bound. The elections don't
   *  count: the bind view collects them. */
  bindAction?: BindAction;
  /** The broker's latest bind request and Signal's decision (BRK-S209). */
  bindRequest?: BrokerBindRequest | null;
  binderId?: string | null;
  policyId?: string | null;
  /** QTE-{brokerage code}-{seq}, kept for the quote's life. */
  quoteNumber?: string | null;
  // ── Its history (PMS-S751) and its conditions (PMS-S749, BRK-S362) ───────
  /** The current version's number; the quote's `id` never changes. */
  version?: number | null;
  /** Every version, newest first, with what changed (not shown yet). */
  versions?: QuoteVersionSummary[];
  /** When the carrier last priced it again with no change. */
  repricedAt?: string | null;
  /** What it's subject to: pre-bind first, oldest first within each. */
  subjectivities?: QuoteSubjectivity[];
  /** The notes shared with the broker, newest first. */
  notes?: QuoteNote[];
  /** Signal's request to the carrier's underwriter, if any. */
  referral?: QuoteReferral | null;
  /** The quote's underwriter review (PMS-S871, BRK-S463): the open one,
   *  else the latest, whoever opened it (the broker, or Signal when it
   *  records a referral, PMS-S870); null when it never had one. Optional for
   *  a PMS before it, and on the select and bind answers. */
  reviewRequest?: ReviewRequest | null;
}

export type QuoteStage =
  | "draft"
  | "offered"
  | "selected"
  | "binding"
  | "bound"
  | "superseded"
  | "expired"
  | "withdrawn"
  | "not_taken";
/** Every stage: the runtime copy the contract test holds to the pin. */
export const QUOTE_STAGES: readonly QuoteStage[] = [
  "draft",
  "offered",
  "selected",
  "binding",
  "bound",
  "superseded",
  "expired",
  "withdrawn",
  "not_taken",
];

export type BindAction = "instant" | "request" | "none";
export const BIND_ACTIONS: readonly BindAction[] = [
  "instant",
  "request",
  "none",
];

/** How many of the submission's class code × state pairs a carrier writes,
 *  refers or declines (PMS-S681) — counts only, never its class keys (BRK-S150). */
export interface AppetiteCounts {
  inAppetite: number;
  referralOnly: number;
  outOfAppetite: number;
}

/** Every outcome a fan-out leg can carry, as the reader knows them. The
 *  submissions pin publishes all but `needs_information`, which the reader
 *  takes ahead of PMS-S706 (BRK-S299; test/lib/brokerSubmissionsContract.test.ts). */
export const INDICATION_OUTCOMES = [
  "quoted",
  "referred",
  "declined",
  "error",
  "appetite_only",
  "needs_information",
] as const;
export type IndicationOutcome = (typeof INDICATION_OUTCOMES)[number];

/** One carrier's leg of the fan-out indication (PMS-S143): its outcome, its
 *  quote when quoted, and its refer/decline/error reasons. `carrierId`
 *  addresses this carrier at the quote step (BRK-S054).
 *
 *  `appetite_only` (PMS-S681): a carrier that prices only when it quotes (Great
 *  American, which has no indication API) answers with its appetite alone —
 *  `quote: null` and `appetite` counts; its price comes from its questions and
 *  quote. `appetite` is null (or absent, from an older PMS) on every other
 *  leg. `needs_information` (PMS-S706, read ahead of the pin) names the fields
 *  this carrier still needs. `readIndicationResults` reads every leg
 *  tolerantly: an outcome it doesn't know is `error`, shown as "Unavailable"
 *  with its reasons, never a crash. */
export interface CarrierIndication {
  carrierId: string;
  carrier: string;
  carrierProfile?: BrokerCarrierSummary | null;
  outcome: IndicationOutcome;
  quote: BrokerQuote | null;
  reasons: string[];
  appetite?: AppetiteCounts | null;
  missingFields?: MissingField[];
}

/** Every outcome of a quote request, as the reader knows them. */
export const QUOTE_OUTCOMES = ["quoted", "referred", "declined"] as const;

/** The outcome of a bindable-quote request (single-carrier until PMS-S145). */
export interface IndicationResult {
  outcome: (typeof QUOTE_OUTCOMES)[number];
  quote: BrokerQuote | null;
  referReasons: string[];
  declineReasons: string[];
}

function isMissingField(value: unknown): value is MissingField {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.key === "string" &&
    typeof v.label === "string" &&
    (v.scope === "account" || v.scope === "submission")
  );
}

/** Extract the structured missing-fields list from a quote-readiness 400
 *  (`details.missingFields`, PMS-S144), or null for any other failure — so the
 *  UI renders a field-level checklist instead of a generic error (NN #7). */
export function missingFieldsOf(err: unknown): MissingField[] | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  if (typeof err.body !== "object" || err.body === null) return null;
  const details = (err.body as Record<string, unknown>).details;
  if (typeof details !== "object" || details === null) return null;
  const list = (details as Record<string, unknown>).missingFields;
  if (!Array.isArray(list)) return null;
  const fields = list.filter(isMissingField);
  return fields.length > 0 ? fields : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A quote as the comparison needs it: addressable, kind-badged, priced and
 *  with its offer-term and plan lists (the rest is held to the pin by the
 *  contract test). Anything less reads as no quote. */
function isBrokerQuote(value: unknown): value is BrokerQuote {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    (value.kind === "indication" || value.kind === "quote") &&
    typeof value.totalPremium === "string" &&
    Array.isArray(value.offerTerms) &&
    Array.isArray(value.paymentPlans)
  );
}

function readAppetite(value: unknown): AppetiteCounts | null {
  if (!isRecord(value)) return null;
  const { inAppetite, referralOnly, outOfAppetite } = value;
  return typeof inAppetite === "number" &&
    typeof referralOnly === "number" &&
    typeof outOfAppetite === "number"
    ? { inAppetite, referralOnly, outOfAppetite }
    : null;
}

/**
 * The fan-out's legs, read tolerantly (BRK-S299). PMS adds outcomes and fields
 * on its own cadence, and a leg the portal can't fully read must still render:
 * - an outcome the portal doesn't know reads as `error` ("Unavailable");
 * - a missing or malformed `reasons` reads as none, keeping only strings;
 * - `appetite` is kept only as the three counts, `missingFields` only as
 *   well-formed fields;
 * - a leg with no carrier name, or one that isn't an object, is skipped.
 * It never throws (NN #7).
 */
export function readIndicationResults(body: unknown): CarrierIndication[] {
  const results = isRecord(body) ? body.results : undefined;
  if (!Array.isArray(results)) return [];
  return results.flatMap((leg): CarrierIndication[] => {
    if (!isRecord(leg) || typeof leg.carrier !== "string") return [];
    const outcome =
      INDICATION_OUTCOMES.find((o) => o === leg.outcome) ?? "error";
    return [
      {
        carrierId: typeof leg.carrierId === "string" ? leg.carrierId : "",
        carrier: leg.carrier,
        ...(leg.carrierProfile !== undefined
          ? { carrierProfile: readCarrierProfile(leg.carrierProfile) }
          : {}),
        outcome,
        quote: isBrokerQuote(leg.quote) ? leg.quote : null,
        reasons: Array.isArray(leg.reasons)
          ? leg.reasons.filter((r): r is string => typeof r === "string")
          : [],
        appetite: readAppetite(leg.appetite),
        ...(Array.isArray(leg.missingFields)
          ? { missingFields: leg.missingFields.filter(isMissingField) }
          : {}),
      },
    ];
  });
}

/** POST /api/broker/submissions/{id}/price-indication — trigger an indication. */
export async function requestPriceIndication(
  request: AuthedRequester,
  id: string,
): Promise<{ results: CarrierIndication[] }> {
  const body = await request<unknown>(
    `${BASE}/${encodeURIComponent(id)}/price-indication`,
    { method: "POST" },
  );
  return { results: readIndicationResults(body) };
}

export interface CarrierDecline {
  reasons: string[];
}

export interface DeclinedCarrier extends CarrierDecline {
  carrierId: string;
  carrier: string;
}

/** GET /api/broker/submissions/{id}/quotes — persisted offers and declines. */
export async function listSubmissionQuotes(
  request: AuthedRequester,
  id: string,
): Promise<{ items: BrokerQuote[]; declined: DeclinedCarrier[] }> {
  const result = await request<{
    items: BrokerQuote[];
    declined?: DeclinedCarrier[];
  }>(`${BASE}/${encodeURIComponent(id)}/quotes`);
  return { ...result, declined: result.declined ?? [] };
}

/** GET /api/broker/submissions/{id}/quotes/{quoteId}/document — Signal's PDF
 *  of a priced quote or indication (PMS-S932, BRK-S509): the current
 *  revision, named by PMS. 404 for a quote that isn't the brokerage's or the
 *  submission's; 409 `quote_not_priced` while it has no price. */
export function getQuoteDocument(
  fetchBlob: AuthedBlobRequester,
  submissionId: string,
  quoteId: string,
): Promise<FileResponse> {
  return fetchBlob(
    `${BASE}/${encodeURIComponent(submissionId)}/quotes/${encodeURIComponent(quoteId)}/document`,
  );
}

// ─── The carriers in play (PMS-S815, BRK-S393) ────────────────────────────────

/** How a carrier quotes: through its API (asked its questions, quoted on
 *  request), or by hand at Signal (`manual`, PMS-S819). */
export const QUOTING_MODES = ["api", "manual"] as const;
export type QuotingMode = (typeof QUOTING_MODES)[number];

/** A carrier the fan-out would call for this submission. `quotingMode` is
 *  null for a mode the portal doesn't know, which fires nothing. A manual
 *  carrier carries the broker's open quote request, else its latest
 *  (PMS-S819, BRK-S395); null with none, and for every API carrier. */
export interface SubmissionCarrier {
  carrierId: string;
  carrierName: string;
  quotingMode: QuotingMode | null;
  manualQuoteRequest: ActionStanding | null;
}

/** The carriers in play, read tolerantly: a row with no id or name is
 *  skipped, an unknown mode read as none (NN #7). */
export function readSubmissionCarriers(body: unknown): SubmissionCarrier[] {
  const items = isRecord(body) ? body.items : undefined;
  if (!Array.isArray(items)) return [];
  return items.flatMap((row): SubmissionCarrier[] =>
    isRecord(row) &&
    typeof row.carrierId === "string" &&
    typeof row.carrierName === "string"
      ? [
          {
            carrierId: row.carrierId,
            carrierName: row.carrierName,
            quotingMode:
              QUOTING_MODES.find((m) => m === row.quotingMode) ?? null,
            manualQuoteRequest: readActionStanding(row.manualQuoteRequest),
          },
        ]
      : [],
  );
}

/** GET /api/broker/submissions/{id}/carriers — exactly the carriers the
 *  fan-out would call, in its order, from PMS's database alone: it never
 *  calls a carrier. The last step reads it first, so every carrier's
 *  questions go out beside the fan-out (PMS-S815). */
export async function getSubmissionCarriers(
  request: AuthedRequester,
  id: string,
): Promise<SubmissionCarrier[]> {
  return readSubmissionCarriers(
    await request<unknown>(`${BASE}/${encodeURIComponent(id)}/carriers`),
  );
}

// ─── Carrier-addressed quote (PMS-S145, BRK-S054) ────────────────────────
// The questions loop (PMS-S661, BRK-S290): a carrier's questions come in rounds.
// Each round of answers is saved, and the carrier's CURRENT set comes back with
// every saved answer prefilled — a carrier with follow-ups (Great American)
// grows its set as answers come in ("how many owners?" → a set per owner).
// Loop until `complete`, then request the quote, which uses the saved
// answers.

/** An answer: text (option LABELS for a select or yes/no — PMS maps them to the
 *  carrier's own codes), or a multi_select's list of option labels. */
export type CarrierAnswerValue = string | string[];

/** Who gave an answer (PMS-S712): the broker themself, or the customer/insured
 *  the broker is keying in for, e.g. over the phone. */
export const ANSWER_SOURCES = ["user", "insured"] as const;
export type AnswerSource = (typeof ANSWER_SOURCES)[number];

/** Every question type the reader knows; a newer PMS's other types read as
 *  text (`CarrierQuestions`). */
export const QUESTION_TYPES = [
  "boolean",
  "select",
  "text",
  "number",
  "multi_select",
] as const;

/** One of the addressed carrier's pre-quote questions, in the NORMALIZED shape
 *  every adapter maps into (PIE eligibility questions, Blitz program schema,
 *  Great American's questionnaire). Questions the submission's field bag already
 *  answers are filtered out server-side — the SPA never re-asks what PMS knows.
 *  An unknown `type` from a newer PMS is read as text, never a crash. */
export interface CarrierQuestion {
  id: string;
  type: (typeof QUESTION_TYPES)[number];
  prompt: string;
  options?: string[];
  group?: string;
  required: boolean;
  /** A follow-up: the question whose answer opened it, shown under it. */
  parentId?: string;
  /** Shows only while the named question's answer is one of these. */
  dependsOn?: { questionId: string; answers: string[] };
  /** The saved answer, prefilled. */
  answer?: CarrierAnswerValue;
  /** Whose saved answer it is. */
  answerSource?: AnswerSource;
  /** `answer` is the application's, not saved yet (PMS-S705): it counts once
   *  a round sends it back, and has no source. */
  prefilled?: boolean;
  /** A saved answer that no longer fits the question (PMS-S744): a location
   *  since removed, a number typed before the question became a choice. The
   *  question comes back unanswered, and the step says the earlier answer no
   *  longer exists (BRK-S344). */
  previousAnswer?: CarrierAnswerValue;
}

/** A carrier's current questions and whether every required one that shows
 *  has an answer. */
export interface CarrierQuestionSet {
  questions: CarrierQuestion[];
  complete: boolean;
  declined?: CarrierDecline;
}

/** A broker's answer to one carrier question. */
export interface QuoteAnswer {
  questionId: string;
  answer: CarrierAnswerValue;
  questionText?: string;
  source?: AnswerSource;
}

const questionsPath = (id: string, carrierId: string) =>
  `${BASE}/${encodeURIComponent(id)}/carriers/${encodeURIComponent(carrierId)}/questions`;

const isAnswerValue = (value: unknown): value is CarrierAnswerValue =>
  typeof value === "string" ||
  (Array.isArray(value) && value.every((v) => typeof v === "string"));

/** A question as the step reads it: a `previousAnswer` that isn't text or a
 *  list of text is dropped (BRK-S344). */
function readQuestion(q: CarrierQuestion): CarrierQuestion {
  if (q.previousAnswer === undefined || isAnswerValue(q.previousAnswer))
    return q;
  const read = { ...q };
  delete read.previousAnswer;
  return read;
}

// A PMS from before the loop (PMS-S661) sends no `complete`. Read such a set as
// complete only when every required question already carries an answer, so the
// broker is never waved through a set nobody answered.
function readQuestionSet(raw: {
  questions?: CarrierQuestion[];
  complete?: boolean;
  declined?: CarrierDecline;
}): CarrierQuestionSet {
  const questions = (raw.questions ?? []).map(readQuestion);
  const complete =
    typeof raw.complete === "boolean"
      ? raw.complete
      : questions.every((q) => !q.required || q.answer !== undefined);
  return {
    questions,
    complete,
    ...(raw.declined ? { declined: raw.declined } : {}),
  };
}

/** GET /api/broker/submissions/{id}/carriers/{carrierId}/questions — the
 *  addressed carrier's current questions, saved answers prefilled. The
 *  carrierId comes from the fan-out results (CarrierIndication). A carrier with
 *  an application of its own (Great American) creates it on the first call. */
export async function getCarrierQuestions(
  request: AuthedRequester,
  id: string,
  carrierId: string,
): Promise<CarrierQuestionSet> {
  return readQuestionSet(
    await request<Partial<CarrierQuestionSet>>(questionsPath(id, carrierId)),
  );
}

/** POST /api/broker/submissions/{id}/carriers/{carrierId}/questions — save a
 *  round of answers on top of the saved ones; the carrier's current set comes
 *  back. An answer that doesn't fit its question is a 400 with
 *  `details.fieldErrors` keyed by question id; the fitting answers are saved. */
export async function answerCarrierQuestions(
  request: AuthedRequester,
  id: string,
  carrierId: string,
  answers: QuoteAnswer[],
): Promise<CarrierQuestionSet> {
  return readQuestionSet(
    await request<Partial<CarrierQuestionSet>>(questionsPath(id, carrierId), {
      method: "POST",
      body: { answers },
    }),
  );
}

/** POST /api/broker/submissions/{id}/carriers/{carrierId}/quote — firm up the
 *  addressed carrier. It uses the answers saved through the loop, with any in
 *  the body on top. The quote supersedes that carrier's indication in the
 *  quotes listing, so its column flips in place. */
export function requestCarrierQuote(
  request: AuthedRequester,
  id: string,
  carrierId: string,
  answers: QuoteAnswer[] = [],
): Promise<IndicationResult> {
  return request<IndicationResult>(
    `${BASE}/${encodeURIComponent(id)}/carriers/${encodeURIComponent(carrierId)}/quote`,
    { method: "POST", body: { answers } },
  );
}

/**
 * A carrier step's failure, read for the place it belongs (PMS-S661, PMS-S660).
 * Each kind is a handled state in that carrier's section — never a generic
 * banner, never an optimistic success (NN #7):
 * - `missingFields`: the submission isn't ready (400 `details.missingFields`);
 * - `fieldErrors`: answers that don't fit, keyed by question id (400);
 * - `incomplete`: the quote was refused while questions remain (409
 *   `carrier_questions_incomplete`) — reload the questions;
 * - `rejected`: the carrier refused a round (422 `carrier_rejected`), read
 *   from its issues in the broker's words (PMS-S743, BRK-S343), else from the
 *   carrier's own. A saved set accompanies a round that PMS persisted;
 *   pricing refusals follow answers already saved through the loop;
 * - `carrier`: Signal's or the carrier's to fix, not the broker's typo (409
 *   `carrier_setup_incomplete` | `carrier_class_ambiguous` |
 *   `carrier_out_of_appetite` | `carrier_unsupported_value`);
 * - `unavailable`: the carrier can't be reached (502 / 504);
 * - `busy`: another request holds the carrier's leg on this submission past
 *   PMS's 25 second wait (409 `carrier_busy`, PMS-S815): retryable, in PMS's
 *   words;
 * - `other`: anything else, with the handled message.
 */
export type CarrierStepError =
  | { kind: "missingFields"; missing: MissingField[] }
  | { kind: "fieldErrors"; fieldErrors: Record<string, string> }
  | { kind: "incomplete" }
  | {
      kind: "rejected";
      messages: string[];
      saved?: boolean;
      set?: CarrierQuestionSet;
    }
  /** Signal's to resolve with the carrier (a setup gap), or the carrier's
   *  answer (out of appetite). `message` is PMS's sentence, written for
   *  Signal's staff: the broker reads `carrierRefusalLine` (BRK-S496). */
  | {
      kind: "carrier";
      code: string;
      message: string;
      classCode: string | null;
      state: string | null;
    }
  /** The application changed after this carrier priced it (PMS-S707): the
   *  broker re-prices it, so it is theirs to act on, not Signal's. */
  | { kind: "outdated" }
  | { kind: "unavailable" }
  | { kind: "busy"; message: string }
  | { kind: "other"; message: string };

const CARRIER_REFUSALS = new Set([
  "carrier_setup_incomplete",
  "carrier_class_ambiguous",
  "carrier_out_of_appetite",
  "carrier_unsupported_value",
]);

/** A carrier's setup refusal in the broker's words (BRK-S496; Nav,
 *  2026-10-28). PMS's own sentence is for Signal's staff ("Pin one on the
 *  carrier's Appetite tab") and never shows: a setup gap is Signal's to sort
 *  out, with nothing for the broker to change; out of appetite is the
 *  carrier's answer, said plainly. */
export function carrierRefusalLine(
  problem: Extract<CarrierStepError, { kind: "carrier" }>,
  carrier: string,
): string {
  const where =
    problem.classCode && problem.state
      ? `class ${problem.classCode} in ${problem.state}`
      : problem.classCode
        ? `class ${problem.classCode}`
        : "this class code";
  const nothingToChange = "You don’t need to change anything.";
  switch (problem.code) {
    case "carrier_class_ambiguous":
      return `${carrier} lists more than one kind of business for ${where}, so Signal needs to confirm which one applies. ${nothingToChange}`;
    case "carrier_setup_incomplete":
      return `Signal is still finishing ${carrier}’s setup, so it can’t quote yet. ${nothingToChange}`;
    case "carrier_out_of_appetite":
      return `${carrier} doesn’t write ${where}.`;
    default:
      return `${carrier} can’t quote this application as it stands, so Signal needs to sort it out. ${nothingToChange}`;
  }
}

function detailsOf(err: ApiError): Record<string, unknown> {
  if (typeof err.body !== "object" || err.body === null) return {};
  const details = (err.body as Record<string, unknown>).details;
  return typeof details === "object" && details !== null
    ? (details as Record<string, unknown>)
    : {};
}

export function carrierStepError(
  err: unknown,
  stage: "questions" | "pricing" = "questions",
): CarrierStepError {
  if (!(err instanceof ApiError))
    return { kind: "other", message: messageFor(err) };
  const missing = missingFieldsOf(err);
  if (missing) return { kind: "missingFields", missing };
  const details = detailsOf(err);
  const code = typeof details.code === "string" ? details.code : null;
  if (
    err.status === 400 &&
    typeof details.fieldErrors === "object" &&
    details.fieldErrors !== null
  ) {
    const fieldErrors = Object.fromEntries(
      Object.entries(details.fieldErrors as Record<string, unknown>).filter(
        (e): e is [string, string] => typeof e[1] === "string",
      ),
    );
    if (Object.keys(fieldErrors).length > 0)
      return { kind: "fieldErrors", fieldErrors };
  }
  if (err.status === 409 && code === "carrier_questions_incomplete")
    return { kind: "incomplete" };
  if (err.status === 422 && code === "carrier_rejected") {
    // Each issue's `message` is the broker's sentence (PMS-S743); `messages`
    // stays the carrier's own words, read only from a PMS that sends no issues.
    const plain = Array.isArray(details.issues)
      ? details.issues.flatMap((issue: unknown) => {
          const message =
            typeof issue === "object" && issue !== null
              ? (issue as Record<string, unknown>).message
              : undefined;
          return typeof message === "string" ? [message] : [];
        })
      : [];
    const messages = Array.isArray(details.messages)
      ? details.messages.filter((m): m is string => typeof m === "string")
      : [];
    return {
      kind: "rejected",
      ...(stage === "pricing" ? { saved: true } : {}),
      ...(Array.isArray(details.questions) &&
      typeof details.complete === "boolean"
        ? {
            saved: true,
            set: readQuestionSet({
              questions: details.questions,
              complete: details.complete,
            }),
          }
        : {}),
      messages: plain.length
        ? plain
        : messages.length
          ? messages
          : [err.message],
    };
  }
  if (err.status === 409 && code === "quote_outdated")
    return { kind: "outdated" };
  if (err.status === 409 && code && CARRIER_REFUSALS.has(code)) {
    return {
      kind: "carrier",
      code,
      message: err.message,
      classCode:
        typeof details.classCode === "string" ? details.classCode : null,
      state: typeof details.state === "string" ? details.state : null,
    };
  }
  if (err.status === 409 && code === "carrier_busy")
    return { kind: "busy", message: messageFor(err) };
  if (err.status === 502 || err.status === 504) return { kind: "unavailable" };
  return { kind: "other", message: messageFor(err) };
}

/** A carrier Submit found with questions left (PMS-S815): `missing` holds its
 *  unanswered question ids, empty when its questions must be read again
 *  because the application changed since. */
export interface IncompleteCarrier {
  carrierId: string;
  carrierName: string;
  missing: string[];
}

/** The carriers off Submit's 409 `carrier_questions_incomplete`, or null for
 *  any other refusal: the quote route's own 409 of that code names no
 *  carriers. A carrier without an id or a name is skipped. */
export function incompleteCarriersOf(err: unknown): IncompleteCarrier[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const details = detailsOf(err);
  if (
    details.code !== "carrier_questions_incomplete" ||
    !Array.isArray(details.carriers)
  )
    return null;
  return details.carriers.flatMap((c: unknown): IncompleteCarrier[] =>
    isRecord(c) &&
    typeof c.carrierId === "string" &&
    typeof c.carrierName === "string"
      ? [
          {
            carrierId: c.carrierId,
            carrierName: c.carrierName,
            missing: Array.isArray(c.missing)
              ? c.missing.filter((m): m is string => typeof m === "string")
              : [],
          },
        ]
      : [],
  );
}

// ─── Rating details (PMS-S142, BRK-S052; class codes from PMS-S137) ───────────

/** A class-code option for the class-code typeahead. */
/** California's dual-wage classes (PMS-S817): the same trade split at an
 *  hourly wage, e.g. 5432 (high) and 5403 (low) carpentry. */
export type WageTier = "high" | "low";
export const WAGE_TIERS: readonly WageTier[] = ["high", "low"];

export interface CodeOption {
  id: string;
  code: string;
  typeName: string;
  friendlyName: string | null;
  description: string | null;
  /** Which of a dual-wage pair this is (PMS-S817, BRK-S394); null for any
   *  other code. Optional while a PMS before S817 leaves it out. */
  wageTier?: WageTier | null;
  /** Signal's plain-text description of the tier, e.g. "$46/hour or more";
   *  null when unset. */
  wageTag?: string | null;
}

/** A code's wage tier in words, then Signal's description (BRK-S394): the
 *  tier is said, never only coloured. Null for a code without one. */
export function wageTagText(
  code: Pick<CodeOption, "wageTier" | "wageTag">,
): string | null {
  const tier =
    code.wageTier === "high"
      ? "High wage"
      : code.wageTier === "low"
        ? "Low wage"
        : null;
  if (!tier) return null;
  const tag = typeof code.wageTag === "string" ? code.wageTag.trim() : "";
  return tag ? `${tier} · ${tag}` : tier;
}

/** A saved code's wage tier and its description. */
export type CodeWage = Required<Pick<CodeOption, "wageTier" | "wageTag">>;

/** Each saved payroll row's code tier, by code id, off the detail's own
 *  `exposures` (PMS-S817): its display refs carry no tier. Empty from a PMS
 *  before S817, or with no rows. */
export function codeWagesFrom(
  detail: Pick<SubmissionDetail, "exposures"> | null,
): Record<string, CodeWage> {
  const out: Record<string, CodeWage> = {};
  for (const row of detail?.exposures ?? []) {
    const code = row.code;
    if (typeof row.codeId !== "string" || !code) continue;
    out[row.codeId] = {
      wageTier:
        code.wageTier === "high" || code.wageTier === "low"
          ? code.wageTier
          : null,
      wageTag: typeof code.wageTag === "string" ? code.wageTag : null,
    };
  }
  return out;
}

/** A chosen class code's name, as its field shows it: the friendly name, else
 *  the description, else its type, since a code read off a document has no
 *  description yet (BRK-S306). Review shows the same (BRK-S349). */
export function classCodeName(c: CodeOption): string {
  return c.friendlyName ?? c.description ?? c.typeName;
}

/** Why a scoped code search came back empty (PMS-S156). `null` for a plain
 *  search miss within a covered state; set only for a SCOPING emptiness:
 *  - `no_state` — no insured state could be resolved (no address / intake state)
 *  - `no_codes_for_state` — no attached State Code Group covers the state. */
export type CodeSearchReason = "no_state" | "no_codes_for_state" | null;

/** GET /api/broker/submissions/{id}/codes — the class-code typeahead SCOPED to a
 *  submission (PMS-S156, replaced the unscoped /api/broker/codes). The server
 *  resolves the product + the state and returns only the codes the product is
 *  in appetite for there; `state` is the state it actually used, and `reason`
 *  explains a scoping-empty page so the SPA can say why. */
export interface SubmissionCodeSearch extends Page<CodeOption> {
  state: string | null;
  reason: CodeSearchReason;
}

/** GET /api/broker/submissions/{id}/codes?search= — submission-scoped class-code
 *  search (PMS-S156). Scoping is server-side off the submission id. WC codes are
 *  rated by each location's state, and bureau states have their own codes, so a
 *  row names its location (PMS-S665, BRK-S291): one of the submission's own
 *  (PMS-S702), any other a 404. A location not saved yet has no id, so its
 *  row names its state instead (BRK-S302); PMS takes one or the other, never
 *  both. Without either, PMS uses the mailing state. */
export function searchSubmissionCodes(
  request: AuthedRequester,
  submissionId: string,
  search: string,
  {
    limit = 20,
    locationId,
    state,
  }: { limit?: number; locationId?: string; state?: string } = {},
): Promise<SubmissionCodeSearch> {
  const qs = new URLSearchParams();
  if (search) qs.set("search", search);
  qs.set("limit", String(limit));
  if (locationId) qs.set("locationId", locationId);
  else if (state) qs.set("state", state);
  return request<SubmissionCodeSearch>(
    `${BASE}/${encodeURIComponent(submissionId)}/codes?${qs.toString()}`,
  );
}

/** PUT /api/broker/submissions/{id}/details — merge a PARTIAL manifest field
 *  bag into the submission's stored details (PMS-S142; replaces the WC-only
 *  /wc-rating). Keys/value shapes come from the product manifest; effectiveDate
 *  rides in the same body and lands on the typed spine column. Returns the
 *  refreshed detail (rated once it has an effective date). */
export function setSubmissionDetails(
  request: AuthedRequester,
  id: string,
  details: Record<string, unknown>,
): Promise<SubmissionDetail> {
  return request<SubmissionDetail>(
    `${BASE}/${encodeURIComponent(id)}/details`,
    { method: "PUT", body: details },
  );
}

/** What Submit answers: the detail PMS stored, and the carriers it recorded a
 *  quote request for now (PMS-S815), each to be requested in parallel. Empty
 *  for an unchanged re-submit, or from a PMS before S815. */
export interface SubmittedDetail extends SubmissionDetail {
  requestedCarrierIds: string[];
}

/** POST /api/broker/submissions/{id}/submit — the wizard's Submit (PMS-S708).
 *  The first moves a draft to Awaiting triage and stamps `submittedAt`; a
 *  later one is an "update submitted". Resolves with the detail PMS stored,
 *  and the carriers whose quote it recorded a request for; it calls no
 *  carrier. A 400 lists what the application still needs
 *  (`missingFieldsOf`); a carrier with questions left is a 409
 *  `carrier_questions_incomplete` (`incompleteCarriersOf`); a bound
 *  submission is a 409 `submission_locked`. */
export async function submitSubmission(
  request: AuthedRequester,
  id: string,
): Promise<SubmittedDetail> {
  const detail = await request<
    SubmissionDetail & { requestedCarrierIds?: unknown }
  >(`${BASE}/${encodeURIComponent(id)}/submit`, { method: "POST" });
  const requested = detail.requestedCarrierIds;
  return {
    ...detail,
    requestedCarrierIds: Array.isArray(requested)
      ? requested.filter((c): c is string => typeof c === "string")
      : [],
  };
}

// ─── The submission's locations (PMS-S702, BRK-S302) ─────────────────────────
// Held on the submission while it is drafted and quoted; the account's premises
// change only at bind (PMS-S713, Nav 2026-10-07). Each is copied from one of the
// account's addresses (`sourceLocationId`) or entered, and its `id` is what a
// payroll row's `locationId` stores. Pinned in the submissions slice.

export type CityLimits = "inside" | "outside";
export type PremisesInterest = "owner" | "tenant";

/** One of the submission's locations, as PMS reads it back. */
export interface SubmissionLocation {
  id: string;
  street: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  county: string | null;
  locNumber: string | null;
  bldNumber: string | null;
  cityLimits: CityLimits | null;
  interest: PremisesInterest | null;
  yearBuilt: number | null;
  /** Square feet, as PMS publishes it: a string. */
  occupiedArea: string | null;
  highestFloor: number | null;
  /** The building-details toggle: null is unanswered, distinct from No. */
  hasBuildingDetails: boolean | null;
  /** The account address it was copied from, if any. */
  sourceLocationId: string | null;
}

/** One location, as written. An entry restates its location: an omitted field
 *  clears, except `sourceLocationId`, which sticks once set. */
export interface SubmissionLocationInput {
  id?: string;
  sourceLocationId?: string;
  street: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  locNumber?: string;
  bldNumber?: string;
  cityLimits?: CityLimits;
  interest?: PremisesInterest;
  yearBuilt?: number;
  occupiedArea?: string;
  highestFloor?: number | null;
  hasBuildingDetails?: boolean | null;
}

/** GET /api/broker/submissions/{id}/locations — the submission's locations. */
export async function getSubmissionLocations(
  request: AuthedRequester,
  id: string,
): Promise<SubmissionLocation[]> {
  const { locations } = await request<{ locations: SubmissionLocation[] }>(
    `${BASE}/${encodeURIComponent(id)}/locations`,
  );
  return locations;
}

/** PUT /api/broker/submissions/{id}/locations — the whole list: PMS keeps the
 *  ids it echoes, adds the rest, and unlinks anyone left out, unless a payroll
 *  row still uses them (409 `location_in_use`). Resolves with what PMS kept,
 *  in the order sent. */
export async function setSubmissionLocations(
  request: AuthedRequester,
  id: string,
  locations: SubmissionLocationInput[],
): Promise<SubmissionLocation[]> {
  const kept = await request<{ locations: SubmissionLocation[] }>(
    `${BASE}/${encodeURIComponent(id)}/locations`,
    { method: "PUT", body: { locations } },
  );
  return kept.locations;
}

// ─── The submission's people (PMS-S740, BRK-S303) ────────────────────────────
// Owners, officers and contacts, held on the submission while it is drafted
// and quoted, and synced to the account's people at bind (PMS-S713). An
// existing account's people change only by endorsement (Nav, 2026-10-08), so a
// submission copies them in rather than editing them. One list with roles, the
// way PMS keeps an account's people (PMS-S178), and the same fields, so the
// sync at bind copies them one to one. PMS-S740 published the routes, and
// they are pinned in the submissions slice.

/** Whether an owner or officer is covered by the WC policy. */
export type WcInclusion = "included" | "excluded";

/** One of the submission's people, as PMS reads them back. */
export interface SubmissionPerson {
  id: string;
  firstName: string;
  lastName: string;
  /** Free-text job title ("CFO"), beside the closed `officerTitle`. */
  title: string | null;
  officerTitle: OfficerTitle | null;
  roles: PersonnelRole[];
  email: string | null;
  phones: PhoneDetail[];
  /** A decimal string on the read, a number on the write. */
  ownershipPercentage: string | null;
  wcInclusion: WcInclusion | null;
  /** A decimal string on the read, a number on the write. */
  annualRemuneration: string | null;
  /** The account person this one was copied from. */
  sourcePersonnelId: string | null;
  locationId: string | null;
  locationValid: boolean | null;
}

/** One of the submission's people, as written. An entry with `id` updates
 *  that person; one without adds a person. Like an account person, an absent
 *  `officerTitle` or `locationId` keeps what is stored and only null clears it; every other
 *  field is replaced with the person. */
export interface SubmissionPersonInput {
  id?: string;
  firstName: string;
  lastName: string;
  title?: string;
  officerTitle?: OfficerTitle | null;
  roles?: PersonnelRole[];
  email?: string;
  phones?: PhoneInput[];
  ownershipPercentage?: number;
  wcInclusion?: WcInclusion;
  annualRemuneration?: number;
  sourcePersonnelId?: string;
  /** Absent keeps the stored location; null clears it. The WC UI omits it. */
  locationId?: string | null;
}

/** GET /api/broker/submissions/{id}/personnel — the submission's people. */
export async function getSubmissionPersonnel(
  request: AuthedRequester,
  id: string,
): Promise<SubmissionPerson[]> {
  const { personnel } = await request<{ personnel: SubmissionPerson[] }>(
    `${BASE}/${encodeURIComponent(id)}/personnel`,
  );
  return personnel;
}

/** PUT /api/broker/submissions/{id}/personnel — the whole list: PMS upserts
 *  by id and unlinks anyone left out. Resolves with what PMS kept. */
export async function setSubmissionPersonnel(
  request: AuthedRequester,
  id: string,
  personnel: SubmissionPersonInput[],
): Promise<SubmissionPerson[]> {
  const kept = await request<{ personnel: SubmissionPerson[] }>(
    `${BASE}/${encodeURIComponent(id)}/personnel`,
    { method: "PUT", body: { personnel } },
  );
  return kept.personnel;
}
